import { z } from 'zod';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  output,
  Signal,
} from '@angular/core';
import {
  TournamentInfoItemConfig,
  TournamentInfoItemVariant,
  TournamentInfoItemComponent,
} from '../tournament-info-item/tournament-info-item.component';
import { TournamentDataModel } from 'src/app/core/services/tournament/model/tournament-data-model';
import { ModalController, Platform } from '@ionic/angular/standalone';
import { ThemeService } from 'src/app/core/services/theme/theme.service';
import { PaymentService } from 'src/app/core/services/payment/payment.service';
import { getModalConfig, shouldShowAsSheetModal } from 'src/app/shared/utils/modal.utils';
import { TournamentStatusData } from 'src/app/core/services/tournament/model/tournament-data-model';
import { TranslocoService } from '@jsverse/transloco';
import { ThemePipe } from '../../../core/pipes/theme.pipe';
import { ButtonComponent } from '../../buttons/button/button.component';
import { TournamentPillComponent } from '../tournament-pill/tournament-pill.component';
import { IconComponent } from '../../icon/icon.component';
import { NgClass, NgStyle } from '@angular/common';
import { CurrencyIconComponent } from '../../currency-icon/currency-icon.component';
import { SplicedBackgroundComponent } from '../../spliced-background/spliced-background.component';
import { TournamentPaymentEventData } from 'src/app/shared/types/payment-events';
import { TournamentStatus } from 'src/app/core/api/player/schemas/tournaments';
import { OperatorService } from 'src/app/core/services/operator/operator.service';
import { GameId } from 'src/app/model/game/game-id';
import { marblesScoreToTimeOrDnf } from 'src/games/phasermarbles/interface/common/race-attempts-panel-view-model';

export const TournamentInfoVariantSchema = z.enum(['full', 'data-and-info']);
export type TournamentInfoVariant = z.infer<typeof TournamentInfoVariantSchema>;

@Component({
  selector: 'app-tournament-info-card',
  templateUrl: './tournament-info-card.component.html',
  styleUrls: ['./tournament-info-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgClass,
    NgStyle,
    IconComponent,
    CurrencyIconComponent,
    TournamentPillComponent,
    TournamentInfoItemComponent,
    ButtonComponent,
    ThemePipe,
    SplicedBackgroundComponent,
  ],
})
export class TournamentInfoCardComponent {
  currentTournamentData = input.required<TournamentDataModel>();
  currentTime = input.required<Date>();
  variant = input<TournamentInfoVariant>('full');
  clickEvent = output<TournamentPaymentEventData>();

  private platform = inject(Platform);
  private modalController = inject(ModalController);
  private translocoService = inject(TranslocoService);
  private themeService = inject(ThemeService);
  private operatorService = inject(OperatorService);
  private paymentService = inject(PaymentService);

  // Play goes inert while any payment intent is in flight — the service latch would drop the
  // second intent silently, and this is the visible half of that guard. Must match the wager
  // cards' isDisabled treatment.
  readonly paymentInFlight = this.paymentService.paymentInFlight;

  private get isMarbles(): boolean {
    return this.operatorService.gameId === GameId.Marbles;
  }

  protected readonly splicedBg = this.themeService.splicedBackgroundPlacement('tournament-card');

  readonly tournamentStatusData: Signal<TournamentStatusData> = computed(() => {
    return this.currentTournamentData().refreshTimeStatus(this.currentTime());
  });

  readonly status: Signal<TournamentStatus> = computed(() => {
    return this.tournamentStatusData().status;
  });

  readonly cardStyle: Signal<[string, string][]> = computed(() => {
    const statusData = this.tournamentStatusData();
    const state = statusData.live ? 'active' : 'inactive';
    const key = 'card-tournament';

    if (this.variant() !== 'full') {
      return [];
    }

    return [
      ['background', key],
      ['border-radius', key],
      ['border', `card-tournament-${state}`],
    ];
  });

  readonly prizePoolShadowStyles = computed((): [string, string][] => {
    return [
      ['type', 'card-tournament-prize-pool-value'],
      ['text-shadow', 'card-tournament-prize-pool'],
    ];
  });

  readonly prizePoolValueStyles = computed((): [string, string][] => {
    return [
      ['type', 'card-tournament-prize-pool-value'],
      ['text-outer-border', 'card-tournament-prize-pool'],
      ['text-color', 'card-tournament-prize-pool-value'],
    ];
  });

  readonly hasPrizeShadow = computed(() => this.themeService.hasWagerCardPrizeShadow());

  readonly pillLabel: Signal<string> = computed(() => {
    const statusData = this.tournamentStatusData();
    return statusData.pillState;
  });

  readonly activeState: Signal<'active' | 'inactive'> = computed(() => {
    const statusData = this.tournamentStatusData();
    return statusData.live ? 'active' : 'inactive';
  });

  readonly isDisabled: Signal<boolean> = computed(() => {
    const statusData = this.tournamentStatusData();
    return statusData.disabled;
  });

  readonly showButtons: Signal<boolean> = computed(() => {
    return this.variant() === 'full';
  });

  readonly hasSecondaryCurrency = computed(() => this.themeService.hasSecondaryCurrency);
  readonly resultsButtonLabel = this.translocoService.translate('components.tournament.results');

  readonly playButtonLabel = computed(() =>
    this.translocoService.translate('components.tournament.play'),
  );

  readonly playButtonAmount = computed(() => this.currentTournamentData().formattedEntryFee);

  readonly infoItems: Signal<TournamentInfoItemConfig[]> = computed(() => {
    const status = this.status();
    const tournamentData = this.currentTournamentData();
    const statusData = this.tournamentStatusData();

    const items: TournamentInfoItemConfig[] = [];

    if (status === 'CREATED') {
      items.push({
        variant: TournamentInfoItemVariant.Default,
        label: this.translocoService.translate('components.tournament.start-date'),
        value: tournamentData.startDate,
        showPulse: false,
      });
    } else if (status === 'ACCEPTING_SCORES') {
      items.push({
        variant: TournamentInfoItemVariant.Time,
        label: this.translocoService.translate('components.tournament.time-left'),
        value: statusData.timeRemaining,
        showPulse: true,
      });
    } else if (status === 'FINISHED') {
      items.push({
        variant: TournamentInfoItemVariant.Time,
        label: this.translocoService.translate('components.tournament.finished'),
        value: tournamentData.finishDate,
        showPulse: false,
      });
    }

    if (
      (status === 'ACCEPTING_SCORES' || status === 'COLLECTING_SCORES' || status === 'FINISHED') &&
      tournamentData.userScore !== null
    ) {
      items.push({
        variant: TournamentInfoItemVariant.Count,
        label: this.translocoService.translate(
          this.isMarbles ? 'components.tournament.your-time' : 'components.tournament.your-score',
        ),
        value: this.isMarbles
          ? marblesScoreToTimeOrDnf(tournamentData.userScore)
          : tournamentData.userScore.toLocaleString(),
        showPulse: false,
      });
    }

    return items;
  });

  readonly prizePoolLabel = this.translocoService.translate('components.tournament.prize-pool');

  readonly totalPrizePool = computed(() => this.currentTournamentData().prizePool);

  readonly ctaResult = computed(() => {
    const data = this.currentTournamentData();
    if (data.status !== 'FINISHED') {
      return undefined;
    }
    const prizeInfo = data.currentPrizeInfo();
    if (prizeInfo.prizeAmount && prizeInfo.position) {
      return this.translocoService.translate('components.tournament.result-win', {
        position: prizeInfo.position,
        amount: prizeInfo.prizeAmount,
      });
    }
    return undefined;
  });

  get cta(): string {
    return this.translocoService.translate(
      this.isMarbles ? 'components.tournament.get-top-time' : 'components.tournament.get-top-score',
    );
  }
  readonly collectingScoresCta = this.translocoService.translate(
    'components.tournament.awaiting-final-results',
  );

  get iconInfoColor(): string {
    return `--card-tournament-icon-info-fill`;
  }

  readonly prizePoolLabelStyles: [string, string][] = [
    ['type', 'card-tournament-prize-pool-label'],
    ['text-color', 'card-tournament-prize-pool-label'],
  ];

  readonly ctaStyles: [string, string][] = [
    ['type', 'card-tournament-status-value'],
    ['text-color', 'card-tournament-status-value'],
  ];

  readonly ctaResultStyles: [string, string][] = [
    ['type', 'card-tournament-status-value'],
    ['text-color', 'card-tournament-status'],
  ];

  public async onTutorialClick(): Promise<void> {
    const { TournamentTutorialComponent } =
      await import('../../tutorial-wizard/feature-tutorials/tournament-tutorial.component');
    const asSheetModal = shouldShowAsSheetModal(this.platform, this.themeService);
    const modal = await this.modalController.create({
      component: TournamentTutorialComponent,
      componentProps: {
        tournamentData: this.currentTournamentData(),
      },
      ...getModalConfig(asSheetModal),
    });
    await modal.present();
    await modal.onDidDismiss();
  }

  public async onViewClick(): Promise<void> {
    if (this.isDisabled()) {
      return;
    }

    const { TournamentLeaderboardViewComponent } =
      await import('../tournament-leaderboard-view/tournament-leaderboard-view.component');
    const asSheetModal = shouldShowAsSheetModal(this.platform, this.themeService);
    const modal = await this.modalController.create({
      component: TournamentLeaderboardViewComponent,
      componentProps: {
        tournamentId: this.currentTournamentData().tournamentId,
      },
      ...getModalConfig(asSheetModal, { mobile: 'custom-modal-height' }),
    });
    await modal.present();
  }

  public async onPlayClick(): Promise<void> {
    if (this.paymentInFlight()) {
      return;
    }
    this.clickEvent.emit({
      type: 'tournament',
      tournamentData: this.currentTournamentData(),
    });
  }
}
